import React from "react";
import { NavLink } from "react-router-dom";
import { FileSpreadsheet, Users as UsersIcon, Building2, Settings, Shield, Plus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { PendenciaDot } from "./pendenciaDocsContext";
import { cedentePath } from "../lib/portal";
import { SACADO_LABEL_SUFFIX } from "../lib/cedenteOperations";
import useCedenteNewOperationGate from "../hooks/useCedenteNewOperationGate";

const NAV = [
  { to: cedentePath("/operacoes"), label: "Operações", icon: FileSpreadsheet, testid: "ced-nav-operacoes" },
  // Gate de entrada (04(a)): `gated` = só aparece com a feature ligada E
  // catálogo com produto habilitado. `roles` = filtro por papel portado do
  // Sidebar.jsx:26-32 (viewer do cedente não cria operação).
  {
    to: cedentePath("/operacoes/nova"),
    label: "Nova Operação",
    icon: Plus,
    testid: "ced-nav-nova-operacao",
    roles: ["cedente_admin", "cedente_operator"],
    gated: true,
  },
  // 09.4: sufixo "(sacados)" BEM discreto no item — só rótulo visual; rota,
  // testid e chave do item continuam iguais.
  { to: cedentePath("/clientes"), label: "Clientes", suffix: SACADO_LABEL_SUFFIX, icon: Building2, testid: "ced-nav-clientes" },
  { to: cedentePath("/usuarios"), label: "Usuários", icon: UsersIcon, testid: "ced-nav-usuarios" },
  { to: cedentePath("/configuracoes"), label: "Configurações", icon: Settings, testid: "ced-nav-configuracoes", pendenciaDocs: true },
];

export default function CedenteSidebar({ open, onClose }) {
  const { user } = useAuth();
  const { visible: newOperationVisible } = useCedenteNewOperationGate();
  // Filtro por papel + gate de feature (molde Sidebar.jsx:26-32).
  const items = NAV.filter((n) => {
    if (n.gated && !newOperationVisible) return false;
    return !n.roles || n.roles.includes(user?.role);
  });
  const initials = (user?.cedente_name || "C")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-white border-r border-[var(--zemo-border)]
          flex flex-col transform transition-transform duration-200 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="cedente-sidebar"
      >
        {/* Brand */}
        <div className="px-6 py-5 border-b border-[var(--zemo-border)] flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-zinc-900 text-white flex items-center justify-center font-bold tracking-tight">
            Z
          </div>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold tracking-tight">Portal Zemo</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium">Cedente</div>
          </div>
        </div>

        {/* Cedente identity */}
        <div className="px-6 py-4 border-b border-[var(--zemo-border)]">
          <div className="zemo-overline mb-1.5">Cedente</div>
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center text-xs font-bold text-emerald-700">
              {initials}
            </div>
            <div className="text-[13px] font-semibold text-zinc-900 leading-tight">
              {user?.cedente_name || "—"}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => onClose && onClose()}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors
                  ${isActive ? "nav-active" : "text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"}`
                }
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
                <span>
                  {item.label}
                  {item.suffix && (
                    <span className="ml-1 text-[10px] text-zinc-400 font-normal">{item.suffix}</span>
                  )}
                </span>
                {item.pendenciaDocs && <PendenciaDot testid={`${item.testid}-pendencia-dot`} className="ml-auto" />}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--zemo-border)] flex items-center gap-2 text-[11px] text-zinc-500">
          <Shield className="h-3.5 w-3.5" />
          <span>Conexão segura · TLS 1.3</span>
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={onClose}
          data-testid="cedente-sidebar-overlay"
        />
      )}
    </>
  );
}
